import { ClaimResponse as Claim } from '@sentara-health/sentara-api-hooks-core';
import { formatYYYYMMDDToMDY, extractISODateFromClaim } from './DateFormat';

export type IndexedClaim = {
  original: Claim;
  serviceType: string;
  providerName: string;
  location: string;
  status: string;
  dateOfService: number;
  normalizedServiceDate: string;
  amountOwed: number;
  memberId: string;
};

// ---- helpers ----
const findExtValue = (claim: Claim, key: string): string => {
  return (
    claim.extension?.find((e: any) => e?.url === key)?.value?.toString().trim() ||
    ''
  );
};

const findMemberId = (claim: Claim): string => {
  return (
    claim.identifier
      ?.find((i: any) =>
        i?.system?.toLowerCase().includes('member/memberid')
      )
      ?.value?.toString().trim() || ''
  );
};

const findPatientResponsibility = (claim: Claim): number => {
  if (!Array.isArray(claim.total)) return 0;

  const bucket = claim.total.find(
    (t: any) =>
      t?.category?.coding?.code === 'totalPatientResponsibility'
  );

  const raw = bucket?.amount?.value;
  if (raw == null) return 0;

  const parsed = Number(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const extractServiceType = (claim: Claim): string => {
  const coding = claim.type?.coding;
  if (!coding) return '';

  if (Array.isArray(coding)) {
    return coding[0]?.display?.toString().trim() || '';
  }

  // coding is object
  if (typeof coding === 'object' && 'display' in coding) {
    return (coding as any).display?.toString().trim() || '';
  }

  return '';
};

// ---- main build function ----
export function buildIndexedClaims(claims: Claim[]): IndexedClaim[] {
  if (!Array.isArray(claims) || claims.length === 0) return [];

  return claims.map((claim) => {
    const serviceType = extractServiceType(claim);
    const providerName = findExtValue(claim, 'providerName');
    const location = findExtValue(claim, 'practiceName');

    const status = claim.status?.toString().trim() || '';

    const dateOfService = claim.created
      ? new Date(claim.created).getTime()
      : 0;

    // Extract ISO date and format for display using shared utility
    const isoDateString = extractISODateFromClaim(claim.created);
    const normalizedServiceDate = formatYYYYMMDDToMDY(isoDateString);

    (claim as any).normalizedServiceDate = normalizedServiceDate;

    const amountOwed = findPatientResponsibility(claim);
    const memberId = findMemberId(claim);

    return {
      original: claim,
      serviceType,
      providerName,
      location,
      status,
      dateOfService,
      normalizedServiceDate,
      amountOwed,
      memberId,
    };
  });
}